import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { fmtMVR } from "@/lib/api";

export default function CartDrawer() {
    const { items, isOpen, setIsOpen, updateQty, removeItem, total } =
        useCart();
    const navigate = useNavigate();

    const handleCheckout = () => {
        if (items.length === 0) return;
        setIsOpen(false);
        navigate("/checkout");
    };

    return (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetContent
                side="right"
                className="w-full sm:max-w-md bg-pz-bg border-l border-pz-border p-0 flex flex-col"
                data-testid="cart-drawer"
            >
                <SheetHeader className="px-6 pt-6 pb-4 border-b border-pz-border">
                    <SheetTitle className="font-display text-3xl text-pz-text flex items-center gap-3">
                        <ShoppingBag className="text-pz-primary" size={24} />
                        Your Cart
                    </SheetTitle>
                    <p className="text-sm text-pz-textMuted text-left">
                        {items.length === 0
                            ? "No items yet — go grab something tasty!"
                            : `${items.length} item${items.length > 1 ? "s" : ""} ready to order`}
                    </p>
                </SheetHeader>

                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
                    {items.length === 0 ? (
                        <div className="text-center py-20">
                            <div className="w-20 h-20 rounded-full bg-pz-bg2 mx-auto grid place-items-center mb-4">
                                <ShoppingBag
                                    size={32}
                                    className="text-pz-textMuted"
                                />
                            </div>
                            <p className="text-pz-textMuted">
                                Your cart is empty
                            </p>
                        </div>
                    ) : (
                        items.map((item) => (
                            <div
                                key={item.id}
                                data-testid={`cart-item-${item.id}`}
                                className="flex gap-4 bg-white rounded-2xl p-3 border border-pz-border"
                            >
                                <img
                                    src={item.image}
                                    alt={item.name}
                                    className="w-20 h-20 rounded-xl object-cover"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-start justify-between gap-2">
                                        <h4 className="font-semibold text-pz-text leading-tight">
                                            {item.name}
                                        </h4>
                                        <button
                                            onClick={() => removeItem(item.id)}
                                            data-testid={`remove-item-${item.id}`}
                                            className="text-pz-textMuted hover:text-pz-primary transition-colors"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                    <p className="text-sm text-pz-primary font-semibold mt-0.5">
                                        {fmtMVR(item.price)}
                                    </p>
                                    <div className="flex items-center justify-between mt-2">
                                        <div className="inline-flex items-center bg-pz-bg2 rounded-full">
                                            <button
                                                onClick={() =>
                                                    updateQty(item.id, -1)
                                                }
                                                data-testid={`qty-dec-${item.id}`}
                                                className="w-8 h-8 grid place-items-center text-pz-text hover:text-pz-primary"
                                            >
                                                <Minus size={14} />
                                            </button>
                                            <span
                                                className="px-3 font-semibold text-pz-text min-w-[2ch] text-center"
                                                data-testid={`qty-${item.id}`}
                                            >
                                                {item.quantity}
                                            </span>
                                            <button
                                                onClick={() =>
                                                    updateQty(item.id, 1)
                                                }
                                                data-testid={`qty-inc-${item.id}`}
                                                className="w-8 h-8 grid place-items-center text-pz-text hover:text-pz-primary"
                                            >
                                                <Plus size={14} />
                                            </button>
                                        </div>
                                        <span className="font-semibold text-pz-text">
                                            {fmtMVR(item.price * item.quantity)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {items.length > 0 && (
                    <div className="border-t border-pz-border p-6 bg-white space-y-4">
                        <div className="flex items-center justify-between">
                            <span className="text-pz-textMuted uppercase tracking-wider text-xs font-semibold">
                                Total
                            </span>
                            <span
                                data-testid="cart-total"
                                className="font-display text-3xl text-pz-primary"
                            >
                                {fmtMVR(total)}
                            </span>
                        </div>
                        <Button
                            onClick={handleCheckout}
                            data-testid="cart-checkout-btn"
                            className="w-full h-12 rounded-full bg-pz-primary hover:bg-pz-primaryHover text-white font-bold uppercase tracking-wider text-sm"
                        >
                            Proceed to Checkout
                        </Button>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
}
